import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Search, ReceiptText, BanknoteArrowUp } from 'lucide-react';
import { fetchOpenTickets } from '../../services/ticketService';
import {
  calculateTicketPricing,
  formatCurrency,
  formatDuration,
  formatHourlyRate,
  formatNamibianDateTime
} from '../../utils/parkingTicketUtils';

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #7f1b1c 0%, #640003 100%);
  padding: 120px 20px 80px;
`;

const Card = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
`;

const Title = styled.h1`
  margin: 0 0 8px;
  color: #1e293b;
`;

const Subtitle = styled.p`
  margin: 0 0 20px;
  color: #64748b;
`;

const Filters = styled.form`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  align-items: end;
  margin-bottom: 20px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-size: 0.875rem;
  color: #334155;
  margin-bottom: 6px;
  font-weight: 600;
`;

const Input = styled.input`
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 0.95rem;
  outline: none;

  &:focus {
    border-color: #c31321;
    box-shadow: 0 0 0 3px rgba(195, 19, 33, 0.1);
  }
`;

const SearchButton = styled.button`
  border: none;
  border-radius: 8px;
  padding: 11px 14px;
  background: #c31321;
  color: #fff;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  gap: 8px;
  justify-content: center;
  align-items: center;
`;

const ErrorText = styled.p`
  margin: 8px 0;
  color: #b91c1c;
  font-weight: 600;
`;

const TableWrap = styled.div`
  overflow: auto;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TH = styled.th`
  text-align: left;
  background: #f8fafc;
  color: #1e293b;
  padding: 10px;
  font-size: 0.85rem;
  white-space: nowrap;
`;

const TD = styled.td`
  padding: 10px;
  border-top: 1px solid #e2e8f0;
  font-size: 0.9rem;
  color: #334155;
  white-space: nowrap;
`;

const RowActions = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  border: none;
  border-radius: 8px;
  padding: 8px 12px;
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  background: #1d4ed8;
`;

const EmptyState = styled.div`
  padding: 28px 12px;
  text-align: center;
  color: #64748b;
`;

const toDateInputValue = (date) => date.toISOString().slice(0, 10);

const OpenTickets = () => {
  const navigate = useNavigate();
  const [key, setKey] = useState('');
  const [dateFrom, setDateFrom] = useState(toDateInputValue(new Date(Date.now() - (30 * 24 * 60 * 60 * 1000))));
  const [dateTo, setDateTo] = useState(toDateInputValue(new Date()));
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastSearchKey, setLastSearchKey] = useState('');

  const ticketRows = useMemo(
    () => tickets
      .map((item) => {
        const attributes = item.attributes || {};
        const pricing = calculateTicketPricing(attributes, lastSearchKey);
        return pricing ? { item, attributes, pricing } : null;
      })
      .filter(Boolean),
    [tickets, lastSearchKey]
  );

  const hasData = ticketRows.length > 0;

  const handleSearch = async (event) => {
    event.preventDefault();
    setError('');
    if (!key.trim()) {
      setError('Please enter a plate key.');
      return;
    }

    setLoading(true);
    const trimmedKey = key.trim();
    setLastSearchKey(trimmedKey);
    try {
      const payload = await fetchOpenTickets({
        key: trimmedKey,
        eventDateAfter: `${dateFrom}T00:00:00`,
        eventDateBefore: `${dateTo}T23:59:59`
      });
      setTickets(Array.isArray(payload.data) ? payload.data : []);
    } catch (searchError) {
      setError(searchError.message || 'Could not load tickets.');
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePay = (ticketRow) => {
    navigate('/smart-parking/open-tickets/pay', {
      state: { ticket: ticketRow.item, plateKey: lastSearchKey }
    });
  };

  return (
    <PageContainer>
      <Card>
        <Title>Open Parking Tickets</Title>
        <Subtitle>
          Search open KPI parking events by plate key and date range, then pay the total payable amount. Tickets close automatically after successful payment.
        </Subtitle>

        <Filters onSubmit={handleSearch}>
          <Field>
            <Label>Plate Key</Label>
            <Input
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="Example: 2583"
            />
          </Field>
          <Field>
            <Label>Date From</Label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </Field>
          <Field>
            <Label>Date To</Label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </Field>
          <SearchButton type="submit" disabled={loading}>
            <Search size={16} />
            {loading ? 'Loading...' : 'Fetch Open Tickets'}
          </SearchButton>
        </Filters>

        {error && <ErrorText>{error}</ErrorText>}

        <TableWrap>
          <Table>
            <thead>
              <tr>
                <TH>Vehicle Plate Number</TH>
                <TH>Parking Slot</TH>
                <TH>Parking Fare</TH>
                <TH>Start Time</TH>
                <TH>End Time</TH>
                <TH>Duration of Stay</TH>
                <TH>Total Payable Amount</TH>
                <TH>Actions</TH>
              </tr>
            </thead>
            <tbody>
              {hasData && ticketRows.map((ticketRow) => {
                const { pricing } = ticketRow;
                const eventId = pricing.eventId;
                return (
                  <tr key={eventId}>
                    <TD>{pricing.vehiclePlate}</TD>
                    <TD>{pricing.parkingSlot}</TD>
                    <TD>{formatHourlyRate(pricing.hourlyRate, pricing.currency)}</TD>
                    <TD>{formatNamibianDateTime(pricing.startDate)}</TD>
                    <TD>{formatNamibianDateTime(pricing.endDate)}</TD>
                    <TD>{formatDuration(pricing.billableHours)}</TD>
                    <TD>{formatCurrency(pricing.totalPayable, pricing.currency)}</TD>
                    <TD>
                      <RowActions>
                        <ActionButton type="button" onClick={() => handlePay(ticketRow)}>
                          <BanknoteArrowUp size={14} /> Pay
                        </ActionButton>
                      </RowActions>
                    </TD>
                  </tr>
                );
              })}
            </tbody>
          </Table>
          {!hasData && !loading && (
            <EmptyState>
              <ReceiptText size={18} style={{ marginBottom: 8 }} />
              No open KPI parking tickets found for this filter.
            </EmptyState>
          )}
        </TableWrap>
      </Card>
    </PageContainer>
  );
};

export default OpenTickets;
